import React, { useCallback } from 'react';
import TableController, { type TableControllerProps } from './TableController';
import {
  ApplicationView,
  StartControllerScope,
  useController,
} from '@aha-app/mvc';
import { ReactEditor, useSlateStatic } from 'slate-react';
import styles from './Table.module.css';
import classnames from 'classnames';
import type TableSelection from '../TableSelection';
import AhaEditorV2Controller from '../../../controllers/AhaEditorV2Controller';
import leftButtonPressed from '../leftButtonPressed';
import './Table.css';
import {
  type ComponentProps,
  type TableElement,
} from '../../../types/aha-editor';
import ColorUtils from '../../../util/color';

const TableComponent: React.FC<TableControllerProps> = ({
  attributes,
  element,
  children,
}) => {
  const columnWidths: number[] = element.table_columnWidths || [];
  const tableWidth = element.table_width || '100%';
  const controller = useController(TableController);
  const path = ReactEditor.findPath(controller.editor, element);
  const cellsSelected =
    controller.state.tableSelection.tableHasSelectedCells(path);
  const hasHeaderRow = element.table_headerRow;
  const hasAlternateRowColor = element.table_alternateRows;
  const hasNumberedRows = element.table_numberedRows;

  const tableBorderColor = element.table_borderColor
    ? ColorUtils.mapColorFromColorPickerToDarkMode(element.table_borderColor)
    : undefined;
  const hasTransparentBorder = element.table_borderColor === 'transparent';

  const onMouseUp = () => {
    controller.actionSetIsSelecting(false);
    controller.clickedAnchorCell = null;
  };

  const onMouseOver = (event: React.MouseEvent) => {
    if (leftButtonPressed(event) && controller.clickedAnchorCell) {
      controller.actionUpdateSelection(event.nativeEvent);
    }
  };

  const onMouseLeave = (event: React.MouseEvent) => {
    if (!leftButtonPressed(event) || !controller.clickedAnchorCell) return;

    const mouseOverDocument = (e: MouseEvent) => {
      controller.actionUpdateSelection(e);
      document.removeEventListener('mouseover', mouseOverDocument);
    };
    document.addEventListener('mouseover', mouseOverDocument);
  };

  // Split children for thead/tbody when hasHeaderRow is true
  const childrenArray = React.Children.toArray(children);
  const headerRow = hasHeaderRow ? childrenArray[0] : null;
  const bodyRows = hasHeaderRow ? childrenArray.slice(1) : childrenArray;

  // Calculate the width needed for numbered row column based on number of digits
  const numBodyRows = bodyRows.length;
  const numDigits = numBodyRows.toString().length;
  const numberedRowColumnWidth = `calc(${numDigits}ch + 16px)`;

  const style: React.CSSProperties = {
    width: tableWidth,
    maxWidth: '100%',
  };
  if (tableBorderColor) {
    style['--table-border-color'] = tableBorderColor;
  }
  if (hasAlternateRowColor && element.table_alternateRowColor) {
    style['--table-alternate-row-color'] = element.table_alternateRowColor;
  }

  return (
    <div
      className="table-wrapper"
      style={{ width: style.width, maxWidth: style.maxWidth }}
    >
      <table
        {...attributes}
        className={classnames('Editor__table', {
          [styles.tableCellsSelected]: cellsSelected,
          'Editor__table--sticky-header': hasHeaderRow,
          'Editor__table--hover-border':
            hasTransparentBorder && !window.isKnowledgeBase,
          'Editor__table--alternate-rows': hasAlternateRowColor,
        })}
        onMouseUp={onMouseUp}
        onMouseOver={onMouseOver}
        onMouseLeave={onMouseLeave}
        style={style}
      >
        <colgroup>
          {hasNumberedRows && <col style={{ width: numberedRowColumnWidth }} />}
          {columnWidths.map((width, index) => (
            <col key={index} style={{ width: `${width}%` }} />
          ))}
        </colgroup>
        {hasHeaderRow && headerRow && (
          <thead className="Editor__thead">{headerRow}</thead>
        )}
        {bodyRows.length > 0 && (
          <tbody className="Editor__tbody">{bodyRows}</tbody>
        )}
      </table>
    </div>
  );
};

const ControlledTableComponent = StartControllerScope(
  TableController,
  ApplicationView(TableComponent)
);

// Include the editor in the controller props.
const WrappedTableComponent: React.FC<ComponentProps<TableElement>> = ({
  attributes,
  element,
  children,
  ...props
}) => {
  const editor = useSlateStatic();
  const editorController = useController(AhaEditorV2Controller);

  const setTableSelection = useCallback(
    (tableSelection: TableSelection) => {
      editorController.actionSetPluginState('tableSelection', tableSelection);
    },
    [editorController]
  );

  return (
    <ControlledTableComponent
      editor={editor}
      attributes={attributes}
      element={element}
      tableSelection={
        editorController.state.pluginState.tableSelection as TableSelection
      }
      setTableSelection={setTableSelection}
      {...props}
    >
      {children}
    </ControlledTableComponent>
  );
};

export default ApplicationView(WrappedTableComponent);
